
import React, { useEffect, useRef } from 'react';

const TechStack: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, { threshold: 0.1 });

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const categories = [
    {
      label: "Cloud & Infrastructure",
      tools: ["AWS", "GCP", "Azure", "Kubernetes", "Terraform", "Docker"],
      color: "bg-[#0091da]/10 text-[#0091da]"
    },
    {
      label: "Modern Web & Mobile",
      tools: ["React", "TypeScript", "Node.js", "Next.js", "Flutter", "GraphQL"],
      color: "bg-[#facc15]/10 text-slate-900"
    },
    {
      label: "Data & Intelligence",
      tools: ["Snowflake", "Python", "PyTorch", "TensorFlow", "PostgreSQL", "dbt"],
      color: "bg-slate-900/5 text-slate-700"
    }
  ];

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-white border-b border-slate-100 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row gap-12 md:gap-20 items-start">
          <div className="lg:w-1/3 reveal">
            <div className="mono text-[10px] text-[#0091da] font-bold mb-6 tracking-[0.4em] uppercase">// Technical Competencies</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 md:mb-8 tracking-tight leading-tight">Our Delivery Ecosystem</h2>
            <p className="text-base md:text-lg text-slate-500 font-light leading-relaxed">
              We specialize in modern architectures that prioritize scalability and enterprise resilience. Our talent grid is specifically mapped to these high-demand stacks.
            </p>
            <div className="mt-8 md:mt-12 h-px w-20 md:w-24 bg-slate-200"></div>
          </div>
          <div className="lg:w-2/3 grid md:grid-cols-3 gap-6 md:gap-8 w-full">
            {categories.map((cat, i) => (
              <div 
                key={i} 
                className="reveal p-6 md:p-8 bg-slate-50 border border-slate-100 rounded-sm hover:shadow-lg transition-all group"
                style={{ transitionDelay: `${(i + 1) * 150}ms` }}
              >
                <h3 className="mono text-[10px] text-slate-400 font-bold uppercase mb-6 md:mb-8 tracking-widest group-hover:text-slate-900 transition-colors">{cat.label}</h3>
                <div className="flex flex-wrap gap-2">
                  {cat.tools.map((tool, j) => (
                    <span key={j} className={`px-3 md:px-4 py-1.5 border border-transparent text-[10px] md:text-[11px] font-bold rounded-sm transition-all uppercase tracking-wider ${cat.color} hover:border-current`}>
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechStack;
